import apiClient from '@/api/client';
import {
  API_LATEST_DESKTOP_APP_RELEASE_ENDPOINT,
  VITE_EXTERNAL_API_HOST,
} from '@/api/config';

interface DesktopAppRelease {
  version: string;
  download_url: string;
}

const DESKTOP_APP_FILE_NAME = 'NPT Desktop App.exe';

const desktopDownloadUrl = (downloadUrl: string): string => {
  if (/^https?:\/\//i.test(downloadUrl)) {
    throw new Error('Desktop app download URL must use the configured API host');
  }

  const apiHost = VITE_EXTERNAL_API_HOST.replace(/\/$/, '');
  const path = downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`;
  return `${apiHost}${path}`;
};

const downloadFileName = (
  contentDisposition: string | undefined,
  fallback: string
): string => {
  const encodedMatch = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1]);
  }

  const fileNameMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
  return fileNameMatch?.[1] ?? fallback;
};

export async function downloadLatestDesktopApp(): Promise<void> {
  const releaseResponse = await apiClient.get<DesktopAppRelease>(
    API_LATEST_DESKTOP_APP_RELEASE_ENDPOINT,
    {
      params: {
        platform: 'windows-x64',
        channel: 'stable',
      },
    }
  );
  const release = releaseResponse.data;
  const packageResponse = await apiClient.get<Blob>(
    desktopDownloadUrl(release.download_url),
    { responseType: 'blob' }
  );

  const url = window.URL.createObjectURL(packageResponse.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = downloadFileName(
    packageResponse.headers['content-disposition'],
    DESKTOP_APP_FILE_NAME
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
